// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-11-17 18:53:19 +0100 (Mon, 17 Nov 2025) $
//  Author    : Olli Powroznik
//  Worker    : $Author: d.rothe $Author: d.rothe
//  Revision  : $Rev: 158292 $
//  Purpose   : frontend title components
// =============================================================================
// ---Third-party imports
import React, {useState, useEffect, useMemo, useRef} from 'react'
import {Menu, Segment, Loader, Item, Input, Header, Label, Image, Dropdown} from 'semantic-ui-react'

import clsx from 'clsx'
import KeyHandler, {KEYDOWN} from 'react-key-handler'

// ---Framework imports
import {get, size} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {TypeCaption} from 'uix/types/components'
import {AutoPortal, Link, LargeThumb, HeaderSegment, XMenu, DebugFeature} from 'uix/components'
import {
  useSelector,
  useResponsive,
  usePersistent,
  useInView,
  useFrontendConfig,
  useAncestorAxis,
} from 'uix/hooks'
import {useImageLocs, getPagesFlat} from 'uix/viewer/hooks'
import {Mordor} from 'uix/viewer/mordor'

// ---Local imports
import {useTitleList, useMinititleInfos} from 'uix/frontend/hooks'
import {FacetPopupMenuItem} from './facets'

// ---Globals
const MAX_TITLES = 20

const TitleDescriptions = ({mods}) => {
  let desc = (name, value) => {
    if (name == 'published') {
      return (
        <div className={name} key={name}>
          In: {value}
        </div>
      )
    }
    return (
      <div className={name} key={name}>
        {value}
      </div>
    )
  }
  let fields = ['subtitle', 'author', 'published', 'origin']
  return fields.filter(n => mods[n]).map(n => desc(n, mods[n]))
}

const TitlesHeader = ({otid}) => {
  let request = useSelector(Sel.request)
  let activeNode = useSelector(root => root.data.tree.activeNode)
  if (!otid) {
    otid = get(activeNode, 'otid')
  }
  let vltypeData = useSelector(root => root.data.tree.byId[otid])

  if (vltypeData && vltypeData.datatype !== 10) {
    let {otid, odid, datatype, subtype, caption} = vltypeData
    return <TypeCaption width="23rem" datatype={datatype} subtype={subtype} caption={caption} />
  } else if (!activeNode) {
    return request.id
  } else {
    return activeNode.title
  }
}

const Thumbs = ({otid, onSelectTitle, thumbZoomed, setThumbZoomed}) => {
  let [ref, onScreen] = useInView({triggerOnce: true})

  let enabled = onScreen
  let imageLocs = useImageLocs({otid, enabled})

  let viewer = null
  if (imageLocs && (imageLocs?.children.length || 0) > 0) {
    let pages = getPagesFlat(imageLocs)

    let options = {layout: {limit: 100}, mouse: {}}

    if (thumbZoomed) {
      options.mouse.wheelModeMeta = 'zoom'
      options.mouse.dragScroll = {x: true, y: true}
      options.layout.limit = null
      options.viewport = {virtualX: null, virtualY: null}
    }

    let onSelectionChange = ({change, selected: nextSelected}) => {
      if (size(change.clicked) > 0) {
        let id = change.clicked[0]
        if (onSelectTitle) {
          onSelectTitle({otid, imageLocs, pageid: id, odid: imageLocs.odid})
        }
      }
    }

    // XXX: resizing (via CSS) and resetting options is broken
    // setting zoomed on viewerId for reinit is kind of lame
    // but this gives us a) the application of above options
    // and not smooth-scroll artefacts
    // it would be nice to fix this

    viewer = (
      <Item.Content className={clsx('thumbs', {zoomed: thumbZoomed})}>
        <KeyHandler
          keyEventName={KEYDOWN}
          code={'Escape'}
          onKeyHandle={() => setThumbZoomed(false)}
        />
        <div className="thumbStripe">
          <Mordor
            options={options}
            imageLocs={imageLocs}
            optionSet="thumbs"
            viewerId={`list_${otid}_${thumbZoomed}`}
            onRectSelect={() => setThumbZoomed(true)}
            onSelectionChange={onSelectionChange}
          />
        </div>
        <Item.Meta>{pages.length} Seiten</Item.Meta>
      </Item.Content>
    )
  }
  return (
    <>
      <span ref={ref} />
      {viewer}
    </>
  )
}

export const Title = ({
  otid,
  caption,
  count,
  details = null,
  onSelectTitle,
  respondMode = [],
  listThumbsOn,
  withThumbs = true,
  thumbWidth,
  link, // build <Link> instead of onSelectTitle
}) => {
  // fetch expensive structured imageLocs after minititleinfo details are available//
  let [thumbZoomed, setThumbZoomed] = useState(false)
  let {showFulltextInfo} = useFrontendConfig('shortTitleinfo')
  let viewer =
    listThumbsOn && size(details) > 0 ? (
      <Thumbs
        otid={otid}
        onSelectTitle={onSelectTitle}
        thumbZoomed={thumbZoomed}
        setThumbZoomed={setThumbZoomed}
      />
    ) : null

  let titleImageClick = details
    ? () => onSelectTitle({odid: details.odid, otid, pageid: details.op_ot_id})
    : null
  let mods = get(details, 'mods', {})
  let title = get(details, 'mods.title', caption)
  let fulltext = details.textstate !== 0 && showFulltextInfo
  let unreleased = details.released === 0

  let onlyComputer = respondMode.includes('onlyComputer')
  let thumbsBelow = thumbZoomed || !onlyComputer
  //~ <Link request={{odid: odid, id: otid}}>{caption}</Link>
  let linkProps = {}

  if (onSelectTitle) {
    linkProps.onClick = () => onSelectTitle({otid})
    linkProps['as'] = 'a'
  }
  let titleLink = (
    <a {...linkProps} title={caption}>
      {title}
    </a>
  )
  if (link) {
    titleLink = (
      <Link id={otid} title={caption}>
        {title}
      </Link>
    )
  }
  // TODO:
  // ++ unreleased ClassName
  return (
    <>
      <li
        style={{display: 'flex'}}
        key={otid}
        className={clsx('mdEntry', count % 2 && 'odd', unreleased && 'unreleased')}
      >
        {withThumbs && (
          <LargeThumb
            unreleased={unreleased}
            width={thumbWidth}
            link={linkProps}
            pageid={details.page?.otid}
            onClick={onSelectTitle ? titleImageClick : null}
            pageRatio={details.page?.height / details.page?.width}
          />
        )}
        <div className="miniTitleInfo">
          <h3 style={{wordBreak: 'break-word'}} className="title">
            {titleLink}
          </h3>
          <TitleDescriptions mods={mods} />
          {fulltext && <div className="fullTextInfo"> Transkription vorhanden</div>}
        </div>
        {!thumbsBelow && viewer}
      </li>
      {thumbsBelow && viewer}
    </>
  )
}

export const Titles = ({
  titles,
  limit,
  onSelectTitle,
  respondMode = [],
  thumbWidth,
  link,
  style,
}) => {
  /* list of <Title>*/
  let {metadataListThumbWidth: thumbWidthCfg, withThumbs} = useFrontendConfig('metadataLists')

  if (!thumbWidth) {
    thumbWidth = thumbWidthCfg
  }

  let sliced = limit ? titles.slice(0, limit) : titles
  // fetch minititle info data for records that have no mods property
  // else assume titles are already enriched
  let titleDetails = useMinititleInfos(sliced.filter(e => !e.mods).map(e => e.otid))
  let items = sliced.map((e, i) => (
    <Title
      {...e}
      count={i}
      key={e.otid}
      details={titleDetails[e.otid] || e}
      onSelectTitle={onSelectTitle}
      respondMode={respondMode}
      thumbWidth={thumbWidth}
      withThumbs={withThumbs}
      link={link}
    />
  ))
  let styles = {margin: 0, padding: 0, ...style}
  return (
    <ul style={styles} id="allDocuments" className={clsx('mdList', ...respondMode)}>
      {items}
    </ul>
  )
}

export const MiniTitleInfo = ({
  otid,
  onSelectTitle,
  basic,
  listThumbsOn,
  thumbWidth,
  className,
  link,
  style,
  ...props
}) => {
  let details = useMinititleInfos(otid)
  let [respondMode, ref] = useResponsive()

  if (details) {
    // data loaded / available
    let title = (
      <Title
        otid={otid}
        details={details}
        respondMode={respondMode}
        onSelectTitle={onSelectTitle ? title => onSelectTitle(title) : undefined}
        thumbWidth={thumbWidth}
        listThumbsOn={listThumbsOn}
        link={link}
      />
    )
    if (basic) {
      return title
    }
    return (
      <div className={className} ref={ref}>
        <Segment className={clsx('title', ...respondMode)} {...props}>
          <Item.Group divided>{title}</Item.Group>
        </Segment>
      </div>
    )
  }
  return null
}

export const PageTitleInfo = ({node, ...props}) => {
  // XXX: needs to check root types on dconfig
  let first_ancestor_md = node.ancestorsAndSelf.filter(e => e.datatype == 1)[0]

  // for slimlinked pages the default ancestor axis is the slimlinked path (as for normal links)
  let enabled_fallback = !first_ancestor_md && [0, 3].includes(node.datatype) && node.slimlink
  let fallback_aaxis_node = useAncestorAxis({otid: node.otid, enabled: enabled_fallback})

  if (!first_ancestor_md) {
    if (enabled_fallback && fallback_aaxis_node) {
      first_ancestor_md = fallback_aaxis_node.ancestorsAndSelf.filter(e => e.datatype == 1)[0]
      if (!first_ancestor_md) {
        return null
      }
    } else {
      return null
    }
  }

  let {otlink, otid} = first_ancestor_md
  return <MiniTitleInfo {...props} otid={otlink || otid} />
}

export const TitleList = ({onSelectTitle}) => {
  /* <Titles> with data via useTitleList()
   */
  let [source, setDataSource] = usePersistent('datasource', 'db')
  let [searchTerm, setSearchTerm] = usePersistent('searchTerm', '')
  let [respondMode, ref] = useResponsive()

  let [subdomains, setSubdomains] = usePersistent('subdomains', true)
  let limit = MAX_TITLES
  let {list_otid, titles, facets, total, loading, LoadMoreIfRendered} = useTitleList({
    subdomains,
    limit,
    searchTerm,
    source,
  })

  if (!titles) {
    return <Loader active />
  }

  let menu = (
    <XMenu>
      <XMenu.Item active={subdomains} icon="sitemap" onClick={() => setSubdomains(!subdomains)} />
      <XMenu.Input
        debounce
        value={searchTerm}
        iconPosition="left"
        placeholder="search"
        icon={'search'}
        onValueChange={value => setSearchTerm(value)}
      />
      <DebugFeature>
        <XMenu.Dropdown
          options={['db', 'index-desc'].map(v => ({
            value: v,
            key: v,
            text: v,
          }))}
          value={source}
          onChange={(e, data) => setDataSource(data.value)}
        />
        <XMenu.PPrintButton data={{limit, total, loading}} toggle />
      </DebugFeature>
      <FacetPopupMenuItem facets={facets} />
    </XMenu>
  )
  let header = <TitlesHeader otid={list_otid} />

  let subheader = `${titles?.length} / ${total}`
  return (
    <HeaderSegment
      major
      flex
      icon="list"
      header={header}
      subheader={subheader}
      menu={menu}
      loading={loading}
    >
      <div ref={ref} style={{flex: 1, overflow: 'auto'}}>
        <Titles titles={titles} onSelectTitle={onSelectTitle} respondMode={respondMode} />
        {LoadMoreIfRendered}
      </div>
    </HeaderSegment>
  )
}
